/**
 * FilterRulesService - Centralized service for processing filterRules from list configurations.
 *
 * This service handles:
 * - Converting scalar defaultValues to FilterOption arrays
 * - Evaluating PermissionConditionNode trees (applyDefaultIf, lockedIf, hiddenIf, boundedIf)
 * - Processing dynamic dates in default values
 * - Identifying locked and hidden filters
 * - Resolving the effective FilterRule when a saved-search override layers over a base rule
 */

import type { FilterOption } from '../../filters/config';
import { processDynamicDatesInObject } from '../../utils/dynamic-date';
import type {
	FilterRule,
	ListConfiguration,
	PermissionConditionNode,
} from '../types/list';

/**
 * Result of processing filterRules for UI state
 */
export interface ProcessedFilterRules {
	/** Filter state for UI - arrays of FilterOption */
	filterState: Record<string, Array<FilterOption>>;
	/** Filters that should be locked (non-editable) */
	lockedFilters: Record<string, Array<FilterOption>>;
	/** Filters that should be hidden from UI */
	hiddenFilters: Record<string, boolean>;
	/** Filters that are bounded — user can select within this set but not outside it */
	boundedFilters: Record<string, Array<FilterOption>>;
	/** Raw default values for API queries (before UI transformation) */
	filterDefaults: Record<string, unknown>;
}

/**
 * Context for evaluating filterRules conditions
 */
export interface FilterRulesContext {
	/**
	 * User permissions for permission-based filter evaluation.
	 * Used to evaluate applyDefaultIf, lockedIf, hiddenIf, boundedIf conditions.
	 */
	permissions?: Array<string>;

	/**
	 * Callback function to check if user has a specific permission.
	 * Takes precedence over permissions array if both are provided.
	 *
	 * @param permission - Permission string to check (without negation prefix)
	 * @returns true if user has the permission
	 */
	hasPermission?: (permission: string) => boolean;
}

/**
 * Centralized service for processing filterRules from list configurations.
 */
export class FilterRulesService {
	/**
	 * Process filterRules from a list configuration.
	 * Returns processed filter state for UI and raw defaults for API.
	 */
	static process(
		list: ListConfiguration,
		context: FilterRulesContext = {},
	): ProcessedFilterRules {
		const filterState: Record<string, Array<FilterOption>> = {};
		const lockedFilters: Record<string, Array<FilterOption>> = {};
		const hiddenFilters: Record<string, boolean> = {};
		const boundedFilters: Record<string, Array<FilterOption>> = {};
		const filterDefaults: Record<string, unknown> = {};

		if (list.filterRules == null) {
			return {
				filterState,
				lockedFilters,
				hiddenFilters,
				boundedFilters,
				filterDefaults,
			};
		}

		for (const [filterType, rule] of Object.entries(list.filterRules)) {
			if (this.isFilterHidden(rule, context)) {
				hiddenFilters[filterType] = true;
			}

			if (!this.shouldApplyDefault(rule, context)) {
				continue;
			}

			if (rule.defaultValue != null) {
				const processedValue = processDynamicDatesInObject({
					value: rule.defaultValue,
				});
				const defaultValue = processedValue.value;

				filterDefaults[filterType] = defaultValue;

				if (Array.isArray(defaultValue)) {
					filterState[filterType] = (defaultValue as Array<unknown>).map((v) =>
						this.toFilterOption(v),
					);
				} else {
					filterState[filterType] = [this.toFilterOption(defaultValue)];
				}

				const lengthBefore = filterState[filterType].length;
				this.mergeAdditionalValues(rule, context, filterState[filterType]);

				if (filterState[filterType].length > lengthBefore) {
					filterDefaults[filterType] = filterState[filterType].map(
						(opt) => opt.data ?? { id: opt.id, label: opt.label },
					);
				}

				if (this.isFilterLocked(rule, context)) {
					lockedFilters[filterType] = filterState[filterType];
				}

				if (this.isFilterBounded(rule, context)) {
					const composedValues = [...filterState[filterType]];

					this.collectNonDefaultBoundedValues(rule, context, composedValues);

					if (composedValues.length === 0) {
						// Fail-closed: no permissions matched, so enforce impossible filter
						// to ensure zero results on the client-side
						const impossibleValue = { id: '-1', label: 'None' };
						filterState[filterType] = [this.toFilterOption(impossibleValue)];
						lockedFilters[filterType] = filterState[filterType];
						filterDefaults[filterType] = [impossibleValue];
					} else {
						boundedFilters[filterType] = composedValues;
					}
				}
			}
		}

		return {
			filterState,
			lockedFilters,
			hiddenFilters,
			boundedFilters,
			filterDefaults,
		};
	}

	/**
	 * Extract only the raw filter defaults for API queries.
	 * Faster alternative to process() when UI state is not needed.
	 */
	static extractDefaults(
		list: ListConfiguration,
		context: FilterRulesContext = {},
	): Record<string, unknown> {
		const filterDefaults: Record<string, unknown> = {};

		if (list.filterRules == null) {
			return filterDefaults;
		}

		for (const [filterType, rule] of Object.entries(list.filterRules)) {
			if (!this.shouldApplyDefault(rule, context)) {
				continue;
			}

			if (rule.defaultValue != null) {
				const processedValue = processDynamicDatesInObject({
					value: rule.defaultValue,
				});
				// `unknown` because additionalValues merging below can widen the
				// resolved array beyond rule.defaultValue's declared shape.
				let resolvedValue: unknown = processedValue.value;

				if (rule.additionalValues != null && Array.isArray(resolvedValue)) {
					const merged = [...(resolvedValue as Array<unknown>)];

					for (const additional of rule.additionalValues) {
						if (additional.applyAsDefault === false) {
							continue;
						}

						if (this.evaluateNode(additional.applyIf, context)) {
							merged.push(...additional.values);
						}
					}

					resolvedValue = merged;
				}

				filterDefaults[filterType] = resolvedValue;
			}
		}

		return filterDefaults;
	}

	/**
	 * Idempotent unwrap of any filterRules-wrapper shape inside an
	 * incoming filter map (PD-13546).
	 *
	 * Saved searches sometimes round-trip filter values that look like the
	 * raw filterRules wrapper `{ defaultValue, additionalValues?,
	 * applyDefaultIf?, boundedIf? }` rather than the EXTRACTED defaults
	 * (an array of selected ids). When that wrapper makes it into
	 * `state.filterState`, downstream serialization sends the whole
	 * `{ defaultValue: [...] }` object to the backend as a filter VALUE,
	 * which produces SQL errors like:
	 *   - "Unknown column 'defaultValue' in 'where clause'"
	 *   - "Invalid filter format for 'orderDateRange'"
	 *   - "syntax error near 'additionalValues' = '[object Object]'"
	 * because the backend treats the wrapper KEYS (defaultValue,
	 * additionalValues, applyDefaultIf) as field/column names.
	 *
	 * Applying this unwrap at every entry point into filter state makes
	 * the apply path idempotent — repeated selections of the same saved
	 * search produce byte-identical outgoing request payloads.
	 *
	 * - Wrapped values are unwrapped to their `defaultValue` (nullish
	 *   becomes an empty array — a saved search that cleared the default
	 *   should land as "no filter applied", not as undefined).
	 * - Already-unwrapped values (arrays, primitives, or domain-shaped
	 *   objects like `{ startDate, endDate }`) pass through untouched.
	 *   The discriminator is the presence of `defaultValue` as an own key,
	 *   which the dateRange / numeric range shapes never carry.
	 *
	 * Never mutates the input — returns a new object.
	 */
	static unwrapFilterRulesShapes(
		filters: Record<string, unknown> | null | undefined,
	): Record<string, unknown> {
		const result: Record<string, unknown> = {};

		if (filters == null) {
			return result;
		}

		for (const [key, value] of Object.entries(filters)) {
			if (this.isWrappedFilterRule(value)) {
				const dv = (value as { defaultValue?: unknown }).defaultValue;
				result[key] = dv ?? [];
			} else {
				result[key] = value;
			}
		}

		return result;
	}

	/**
	 * Detect the filterRules wrapper shape produced by saved-search
	 * config persistence. The wrapper always carries `defaultValue` (and
	 * may also carry additionalValues / applyDefaultIf / boundedIf).
	 * Plain extracted values (arrays of ids) and domain objects
	 * (`{ startDate, endDate }`, `{ min, max }`) never have `defaultValue`
	 * as an own key, so we use that as the discriminator.
	 */
	private static isWrappedFilterRule(value: unknown): boolean {
		if (typeof value !== 'object' || value === null || Array.isArray(value)) {
			return false;
		}

		return Object.prototype.hasOwnProperty.call(value, 'defaultValue');
	}

	/**
	 * Merge a saved-search override rule onto a base (bundled) rule per-field.
	 * Override fields win when explicitly set (non-undefined); missing fields
	 * fall through to the base rule. Returns a new object — neither input is mutated.
	 *
	 * Use this when resolving the effective FilterRule for a saved-search-backed
	 * list, so v1 saved-search edits (defaultValue/locked/display overrides) inherit
	 * Advanced fields (applyDefaultIf/boundedIf/additionalValues/etc.) from the bundled
	 * rule automatically. As the override gains more fields in v1b+, it transparently
	 * starts winning on those fields too.
	 */
	static resolveFilterRule(
		baseRule: FilterRule | undefined,
		overrideRule: FilterRule | undefined,
	): FilterRule {
		if (baseRule == null && overrideRule == null) {
			return {};
		}

		if (overrideRule == null) {
			return { ...baseRule };
		}

		if (baseRule == null) {
			return { ...overrideRule };
		}

		// Per-field merge: override wins where defined, base fills the rest.
		const resolved: FilterRule = { ...baseRule };

		for (const key of Object.keys(overrideRule) as Array<keyof FilterRule>) {
			const value = overrideRule[key];
			if (value !== undefined) {
				(resolved as Record<string, unknown>)[key] = value;
			}
		}

		return resolved;
	}

	/**
	 * Check if a filter's default should be applied based on applyDefaultIf condition.
	 * If null/undefined, default applies to all users.
	 */
	private static shouldApplyDefault(
		rule: FilterRule,
		context: FilterRulesContext,
	): boolean {
		if (rule.applyDefaultIf == null) {
			return true;
		}

		return this.evaluateNode(rule.applyDefaultIf, context);
	}

	/**
	 * Check if a filter should be locked (non-editable).
	 */
	static isFilterLocked(
		rule: FilterRule,
		context: FilterRulesContext,
	): boolean {
		if (rule.locked === true) {
			return true;
		}

		if (rule.lockedIf == null) {
			return false;
		}

		return this.evaluateNode(rule.lockedIf, context);
	}

	/**
	 * Check if a filter should be bounded.
	 * When bounded, user can only select from the composed default+additional values.
	 */
	static isFilterBounded(
		rule: FilterRule,
		context: FilterRulesContext,
	): boolean {
		if (rule.boundedIf == null) {
			return false;
		}

		return this.evaluateNode(rule.boundedIf, context);
	}

	/**
	 * Check if a filter should be hidden from UI.
	 */
	private static isFilterHidden(
		rule: FilterRule,
		context: FilterRulesContext,
	): boolean {
		if (rule.hidden === true) {
			return true;
		}

		if (rule.hiddenIf == null) {
			return false;
		}

		return this.evaluateNode(rule.hiddenIf, context);
	}

	/**
	 * Merge additionalValues into the filter state when their conditions are met.
	 * Entries with applyAsDefault: false are skipped here (only appear in bounded set).
	 */
	private static mergeAdditionalValues(
		rule: FilterRule,
		context: FilterRulesContext,
		filterOptions: Array<FilterOption>,
	): void {
		if (rule.additionalValues == null) {
			return;
		}

		for (const additional of rule.additionalValues) {
			if (additional.applyAsDefault === false) {
				continue;
			}

			if (this.evaluateNode(additional.applyIf, context)) {
				for (const value of additional.values) {
					filterOptions.push(this.toFilterOption(value));
				}
			}
		}
	}

	/**
	 * Collect additionalValues with applyAsDefault: false into the bounded set.
	 * These values are not applied as defaults but should still be selectable when bounded.
	 */
	private static collectNonDefaultBoundedValues(
		rule: FilterRule,
		context: FilterRulesContext,
		boundedOptions: Array<FilterOption>,
	): void {
		if (rule.additionalValues == null) {
			return;
		}

		for (const additional of rule.additionalValues) {
			if (additional.applyAsDefault !== false) {
				continue;
			}

			if (this.evaluateNode(additional.applyIf, context)) {
				for (const value of additional.values) {
					boundedOptions.push(this.toFilterOption(value));
				}
			}
		}
	}

	/**
	 * Recursively evaluate a PermissionConditionNode against the current user's permissions.
	 *
	 * - rule node: returns whether user has the action (flipped if negated)
	 * - group node: combines children via AND/OR; flips final result if negated
	 * - empty group: returns true (an unconfigured condition slot doesn't gate anything)
	 *
	 * If no permission info is provided in context, returns true (used in non-permission
	 * code paths that share the same evaluation surface).
	 */
	static evaluateNode(
		node: PermissionConditionNode | null | undefined,
		context: FilterRulesContext,
	): boolean {
		if (node == null) {
			return true;
		}

		const { permissions, hasPermission } = context;

		if (permissions == null && hasPermission == null) {
			return true;
		}

		if (node.type === 'rule') {
			let userHasPermission: boolean;

			if (hasPermission != null) {
				userHasPermission = hasPermission(node.action);
			} else if (permissions != null) {
				userHasPermission = permissions.includes(node.action);
			} else {
				return true;
			}

			return node.negated ? !userHasPermission : userHasPermission;
		}

		// Group node — evaluate children
		if (node.children.length === 0) {
			return true;
		}

		let result: boolean;

		if (node.joiner === 'AND') {
			result = node.children.every((child) =>
				this.evaluateNode(child, context),
			);
		} else {
			result = node.children.some((child) => this.evaluateNode(child, context));
		}

		return node.negated ? !result : result;
	}

	/**
	 * Convert any value to a FilterOption for UI state.
	 */
	private static toFilterOption(value: unknown): FilterOption {
		if (typeof value === 'object' && value !== null) {
			const obj = value as Record<string, unknown>;

			if (obj.id != null && 'label' in obj && 'data' in obj) {
				return value as FilterOption;
			}

			let id: string | number;

			if (obj.id != null) {
				id = obj.id as string | number;
			} else if (obj.value != null) {
				id = String(obj.value);
			} else {
				id = JSON.stringify(value);
			}

			let label: string;

			if (typeof obj.label === 'string') {
				label = obj.label;
			} else if (typeof obj.name === 'string') {
				label = obj.name;
			} else if (obj.id != null) {
				label = String(obj.id);
			} else if (obj.value != null) {
				label = String(obj.value);
			} else {
				label = '';
			}

			return {
				id,
				label,
				data: value,
			};
		}

		return {
			id: String(value),
			label: String(value),
		};
	}
}
